import React, { useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import {
  FilterFieldsContext,
  QueryContext,
  UserContext,
} from '../../../hooks/Contexts';
import * as fieldType from '../../fields/constants/subtypes';
import QuotaNotifyStatus from '../quotas/QuotaNotifyStatus';
import {
  createResponseOption,
  destroyResponseOption,
  orderResponses,
  updateResponseOption,
} from '../constants/handleResponseData';
import {
  createResponse,
  updateResponse,
} from '../constants/handleResponseData';
import { notify } from '../../../helpers/notifications';
import { decodeHTML } from 'entities';

// TODO: refactor this file...

const EditableResponse = ({ field, response, setResponses }) => {
  const { filter_fields, reloadFormQuotaData } =
    useContext(FilterFieldsContext);
  const { queryParams, setQueryParams } = useContext(QueryContext);
  const [showQuotaStatusModal, setShowQuotaStatusModal] = useState(false);
  const [quotaOpt, setQuotaOpt] = useState(null);
  const [prevQuotaOpt, setPrevQuotaOpt] = useState(null);
  const stripResponseValueHTML = () => {
    if (field.type === 'select') {
      return response.response_options.length
        ? field.options.find(
            (opt) => opt.id === response.response_options[0].option_id
          ).id
        : '';
    }
    return response.value ? decodeHTML(response.value) : '';
  };

  const [responseValue, setResponseValue] = useState(stripResponseValueHTML());
  const displayAdminQuotas =
    field.isAdminField && field.options.some((o) => o.local_quota);

  const refreshFilteredSubs = (field, valueOrId) => {
    let fieldId = field.id.toString();
    let filteringByField = Object.keys(queryParams.filters).includes(fieldId);
    if (!filteringByField) return;
    // return if not filtering by this field; the rest is irrelevant
    let filterIncludesValue = false;
    if (fieldType.isOptionType(field.type)) {
      filterIncludesValue = queryParams.filters[fieldId].includes(valueOrId);
    } else {
      let rValue = valueOrId ? valueOrId.toString().toLowerCase() : '';
      filterIncludesValue = rValue.includes(
        queryParams.filters[fieldId].toLowerCase()
      );
    }
    // trigger reload if filtering by edited field response and filter doesn't include new value
    if (!filterIncludesValue) {
      setQueryParams((qp) => {
        return { ...qp };
      });
    }
  };

  const closeQuotaModal = () => {
    setQuotaOpt(null);
    setPrevQuotaOpt(null);
    setShowQuotaStatusModal(false);
  };

  const QuotaModalPortal = () => {
    return createPortal(
      <div onClick={handleClick}>
        <QuotaNotifyStatus
          showQuotaStatusModal={showQuotaStatusModal}
          toggleQuotaStatusModal={closeQuotaModal}
          quotaOpt={quotaOpt}
          prevQuotaOpt={prevQuotaOpt}
        />
      </div>,
      document.getElementById('modal-wrapper')
    );
  };

  const handleClick = (e) => e.stopPropagation();

  // TODO: separate this function into more specific functions
  const handleUpdate = async (e, response) => {
    let r = response;
    let optId = e.target.type === 'select-one' ? e.target.value : null;
    let value = e.target.type === 'select-one' ? null : e.target.value;
    let successful = false;
    // let quota_closure = false;
    let prevOpt = null;

    // Check if response is real or a dummy (generated in orderResponses)
    if (e.target.id.includes('missing-')) {
      // Create a real response
      const { submission_id, field_id } = r;
      let newResponse = await createResponse(submission_id, field_id, value);
      r = newResponse.data;

      if (e.target.type != 'select-one') {
        // return here if a text response was created
        // or, continue to response_options with new r
        return setResponses((responses) => {
          let removeOldRes = responses.filter((rs) => rs.field_id != field_id);
          return orderResponses([...removeOldRes, r], filter_fields);
        });
      }
    }

    // Update response option
    if (e.target.type === 'select-one') {
      let resOpt = r.response_options.length ? r.response_options[0] : null;
      let ro = { status: 0 };

      if (resOpt && optId && resOpt.option_id != optId) {
        ro = await updateResponseOption(resOpt.id, optId);
        r.response_options = [ro.data.response_option];
      } else if (resOpt && !optId) {
        ro = await destroyResponseOption(resOpt.id);
        r.response_options = [];
      } else if (!resOpt && optId) {
        ro = await createResponseOption(r.id, optId);
        r.response_options = [ro.data.response_option];
      }
      successful = [200, 201, 204].includes(ro.status);
      if (successful) {
        // quota_closure = ro.data.quota_closure;
        prevOpt = ro.data?.prev_opt;

        setResponseValue(optId);
        refreshFilteredSubs(field, optId ? optId.toString() : '');
      }
    } else {
      // Update text response
      const currentDecodedValue = response.value
        ? decodeHTML(response.value)
        : '';
      if (currentDecodedValue == value) return;
      let updatedResponse = await updateResponse(r.id, value);
      successful = updatedResponse.status === 200;
      if (successful) {
        r = updatedResponse.data;
        let htmlSafeValue = r.value ? decodeHTML(r.value) : '';
        setResponseValue(htmlSafeValue);
        if (htmlSafeValue != value) {
          notify.info('Note: HTML tags or extra spaces have been removed', {
            showOnlyTheLastOne: false,
            timeout: 6000,
          });
        }
        refreshFilteredSubs(field, htmlSafeValue);
      }
    }

    if (successful) {
      if (field.informs_global) {
        // trigger subs reload to update quota numbers
        if (prevOpt || r?.response_options[0]?.option) {
          // was using quota_closure within conditional above also...
          // decided to leave commented out for now and display every quota opt change
          // Notification Modal if PrevOption or new Option assigned
          setQuotaOpt(r?.response_options[0]?.option);
          setPrevQuotaOpt(prevOpt);
          setShowQuotaStatusModal(true);
        }
        reloadFormQuotaData();
      } else if (displayAdminQuotas) {
        reloadFormQuotaData();
      } else {
        // no subs reload, set new response manually
        setResponses((responses) => {
          let removeOldRes = responses.filter(
            (rs) => rs.field_id != r.field_id
          );
          return orderResponses([...removeOldRes, r], filter_fields);
        });
      }
      // notify Success here
    } else {
      // notify Errors here
    }
  };

  const optWithQuotaCounts = (option) => {
    return `${option.name} - (${option.response_options_count}/${option.local_quota})${option.hidden ? ' - Hidden from public' : ''}`;
  };

  return (
    <td className='editable-data'>
      {field.type === 'text' && (
        <div className='text-input'>
          <input
            id={response.id}
            type='text'
            value={responseValue}
            onChange={(e) => setResponseValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleUpdate(e, response);
            }}
            onBlur={(e) => handleUpdate(e, response)}
            onClick={handleClick}
          />
        </div>
      )}
      {field.type === 'select' && (
        <div className='select-input'>
          <select
            id={response.id}
            value={responseValue}
            onChange={(e) => handleUpdate(e, response, field)}
            onClick={handleClick}
            style={{ backgroundColor: responseValue ? '#fff' : '#efbfc0' }}
          >
            <option label='Please select' value='' />
            {field.options.map((opt) => {
              return (
                <option
                  key={opt.id}
                  label={
                    field.informs_global || displayAdminQuotas
                      ? optWithQuotaCounts(opt)
                      : opt.name
                  }
                  value={opt.id}
                />
              );
            })}
          </select>
          {field.informs_global && <QuotaModalPortal />}
        </div>
      )}
    </td>
  );
};

const SubmissionResponse = ({ submission, field, response, setResponses }) => {
  const user = useContext(UserContext);
  const displayEditable = () => {
    if (field.quick_edit) {
      if (submission.read || field.unread_quick_edit) {
        if (user.canWrite || field.universal_quick_edit) {
          return true;
        }
      }
    }
    return false;
  };

  const displayableValue = (field, response) => {
    if (fieldType.isTextType(field.type)) {
      return response.value || '';
    } else if (fieldType.isOptionType(field.type)) {
      let resOptionIds = response.response_options.map((ro) => ro.option_id);
      let optionValues = field.options
        .filter((opt) => resOptionIds.includes(opt.id))
        .sort((optA, optB) => {
          if (field.alpha) {
            return optA.value.localeCompare(optB.value);
          } else {
            return optA.sort - optB.sort;
          }
        });

      if (field.type === 'checkbox') {
        return optionValues.map((opt) => opt.value) || '';
      } else {
        return optionValues.length ? optionValues[0].value : '';
      }
    }
  };

  return (
    <>
      {displayEditable() ? (
        <EditableResponse
          field={field}
          response={response}
          setResponses={setResponses}
        />
      ) : (
        <td className='index-view'>
          <div>
            {fieldType.isListType(field.type) ? (
              <>
                {field.type === 'file' && (
                  <ul className='manage-file-list-display'>
                    {response.response_files.map((rf) => {
                      return (
                        <li key={rf.id}>
                          <a
                            target='_blank'
                            href={`/manage/response_files/${rf.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {rf.filename
                              ? decodeHTML(rf.filename)
                              : '[Unknown filename]'}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {field.type === 'checkbox' && (
                  <ul>
                    {displayableValue(field, response).map((value, index) => (
                      <li key={response.id + '-' + index}>{value}</li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <p>{decodeHTML(displayableValue(field, response))}</p>
            )}
          </div>
        </td>
      )}
    </>
  );
};

export default SubmissionResponse;
